import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import api from '../lib/api';

/**
 * Composant d'autocomplete pour la recherche de lieux via le système multi-provider (OSM/Mapbox)
 * Supporte la zone CEDEAO (15 pays d'Afrique de l'Ouest)
 * 
 * @param {Object} props
 * @param {string} props.label - Label du champ
 * @param {string} props.placeholder - Placeholder du champ
 * @param {Function} props.onLocationSelect - Callback appelé quand un lieu est sélectionné
 * @param {string} props.country - Code pays ISO ou liste (ex: "CI,GH,SN"). Vide = zone CEDEAO complète
 * @param {string} props.value - Valeur affichée dans le champ
 */
export default function LocationAutocomplete({
  label = "Lieu",
  placeholder = "Rechercher un lieu...",
  onLocationSelect,
  country = "",  // Vide = zone CEDEAO complète (15 pays)
  value = ""
}) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [provider, setProvider] = useState('');
  const debounceTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  // Fermer la liste si on clique en dehors
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fonction de recherche avec debounce
  const searchLocations = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    try {
      // Utiliser l'endpoint GET du nouveau système multi-provider
      const params = new URLSearchParams({
        q: searchQuery,
        limit: '5'
      });
      
      // Ajouter le filtre pays seulement si spécifié
      if (country) {
        params.append('country', country);
      }
      // Si vide, l'API recherchera dans toute la zone CEDEAO

      const response = await api.get(`/geo/search?${params}`);

      if (response.data.success) {
        setSuggestions(response.data.suggestions);
        setProvider(response.data.provider);
        setIsOpen(true);
      } else {
        console.warn('⚠️ Recherche échouée:', response.data.error);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('❌ Erreur recherche lieux:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Effet pour le debounce de la recherche
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchLocations(query);
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [query, country]);

  // Mettre à jour la valeur si elle change de l'extérieur
  useEffect(() => {
    if (value !== query) {
      setQuery(value);
    }
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
  };

  const handleLocationSelect = (suggestion) => {
    // Extraire le nom court (première partie avant la virgule)
    const shortName = suggestion.label.split(',')[0].trim();
    setQuery(shortName);
    setSuggestions([]);
    setIsOpen(false);

    if (onLocationSelect) {
      onLocationSelect({
        label: suggestion.label,
        shortName: shortName,
        latitude: suggestion.latitude,
        longitude: suggestion.longitude,
        place_id: suggestion.place_id,
        place_type: suggestion.place_type,
        provider: provider
      });
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  // Formater le type de lieu
  const formatPlaceType = (type) => {
    const types = {
      'city': 'Ville',
      'town': 'Ville',
      'village': 'Village',
      'suburb': 'Quartier',
      'neighbourhood': 'Quartier',
      'locality': 'Localité',
      'place': 'Lieu',
      'administrative': 'Admin',
      'road': 'Route',
      'address': 'Adresse'
    };
    return types[type] || type || 'Lieu';
  };

  return (
    <div className="space-y-2" ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className="pl-10 pr-10"
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 animate-spin" />
          )}
        </div>
        
        {/* Liste de suggestions */}
        {isOpen && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-[300px] overflow-y-auto">
            {/* Badge provider */}
            <div className="px-3 py-1.5 bg-gray-50 border-b text-xs text-gray-500 flex items-center justify-between">
              <span>{suggestions.length} résultat(s)</span>
              <span className={`px-1.5 py-0.5 rounded text-xs ${provider === 'openstreetmap' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {provider === 'openstreetmap' ? 'OSM' : 'Mapbox'}
              </span>
            </div>
            
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.place_id || index}-${index}`}
                onClick={() => handleLocationSelect(suggestion)}
                className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 flex items-start space-x-3"
              >
                <MapPin className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {suggestion.label.split(',')[0]}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {suggestion.label}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatPlaceType(suggestion.place_type)} • {suggestion.latitude.toFixed(4)}, {suggestion.longitude.toFixed(4)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
        
        {/* Message si pas de résultats */}
        {isOpen && !isLoading && query.length >= 2 && suggestions.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4 text-center text-gray-500 text-sm">
            Aucun lieu trouvé pour "{query}"
          </div>
        )}
      </div>
    </div>
  );
}
